import { Move } from '@app/move/interfaces/move';
import { Movers } from '@app/move/interfaces/movers';
import { Order } from '@app/order/interfaces/order';
import { TelecomTransfer } from '@app/wizard/telecom/interfaces/telecom-transfer';
import { EnergyType } from '@shared/move/enums/energy-type.enum';
import {
    ArrayUtils,
    CreatedByFlowEnum,
    DateUtils,
    DbUtils,
    EnergyMetersState,
    FeatureScope,
    MoveTransactionType,
    NpsReview,
    ObjectUtils,
    ReviewSource,
} from '@smooved/core';
import { ContactLogType } from '../enums/contact-log-type.enum';
import { Leaver } from '../enums/leaver.enum';
import { MoverRole } from '../enums/mover-role.enum';
import { TodoType } from '../enums/todo-type.enum';
import { Transferee } from '../enums/transferee.enum';
import { ContactLog } from '../interfaces/contact-log';
import { User } from '../interfaces/user';

export class MoveUtils {
    public static isTransferee({ user }: Move): boolean {
        return user?.role === MoverRole.Transferee;
    }

    public static isLeaver({ user }: Move): boolean {
        return user?.role === MoverRole.Leaver;
    }

    public static isElectricityOrBoth(move: Move): boolean {
        return [EnergyType.Electricity, EnergyType.Both].includes(move?.energyOffer?.energyOfferSelection?.type);
    }

    public static isCreatedByFlow(move: Move, flow: CreatedByFlowEnum): boolean {
        return ObjectUtils.get(move.createdByFlow, flow);
    }

    public static isGasOrBoth(move: Move): boolean {
        return [EnergyType.Gas, EnergyType.Both].includes(move?.energyOffer?.energyOfferSelection?.type);
    }

    public static hasOnlyMobile(move: Move): boolean {
        return (
            !!move?.telecomOffer?.telecomOfferSelection?.mobilePhone &&
            !move?.telecomOffer?.telecomOfferSelection?.fixedLine &&
            move?.telecomOffer?.mobileBundle?.length > 0
        );
    }

    public static hasOnlyFixedLine(move: Move): boolean {
        return (
            !move?.telecomOffer?.telecomOfferSelection?.mobilePhone &&
            !!move?.telecomOffer?.telecomOfferSelection?.fixedLine &&
            !!move?.telecomOffer?.fixedLine
        );
    }

    public static hasMobileAndFixedLine(move: Move): boolean {
        return (
            !!move?.telecomOffer?.telecomOfferSelection?.mobilePhone &&
            !!move?.telecomOffer?.telecomOfferSelection?.fixedLine &&
            move?.telecomOffer?.mobileBundle?.length > 0 &&
            !!move?.telecomOffer?.fixedLine
        );
    }

    public static showMeterReadingElectricity(move: Move): boolean {
        const energyType = move.energyOffer?.energyType;

        switch (energyType) {
            case EnergyType.Gas:
                return false;

            case EnergyType.Both:
            case EnergyType.Electricity:
            default:
                return true;
        }
    }

    public static energyOfferSelected = (move: Move): boolean => {
        return !!move?.energyOffer?.energyOfferSelection && !!move?.energySelected;
    };

    public static showMeterReadingGas = (move: Move): boolean => {
        const energyType = move.energyOffer?.energyType;

        switch (energyType) {
            case EnergyType.Electricity:
                return false;

            case EnergyType.Both:
            case EnergyType.Gas:
            default:
                return true;
        }
    };

    public static energyComplete = (
        eanCodesComplete: boolean,
        meterReadingsComplete: boolean,
        energyDocumentsMovingAddressByAdmin: boolean,
    ): boolean => {
        return (!!eanCodesComplete && !!meterReadingsComplete) || !!energyDocumentsMovingAddressByAdmin;
    };

    public static getFirstCallInContactLogs = (contactLogs: ContactLog[]): ContactLog => {
        if (!contactLogs) return null;
        return contactLogs.find((contactLog) => contactLog.value === ContactLogType.Call);
    };

    public static getSecondCallInContactLogs = (contactLogs: ContactLog[]): ContactLog => {
        if (!contactLogs) return null;
        const calls = contactLogs.filter((contactLog) => contactLog.value === ContactLogType.Call);
        return calls[1];
    };

    public static getFirstVoicemailInContactLogs = (contactLogs: ContactLog[]): ContactLog => {
        if (!contactLogs) return null;
        return contactLogs.find((contactLog) => contactLog.value === ContactLogType.Voicemail);
    };

    public static getSecondVoicemailInContactLogs = (contactLogs: ContactLog[]): ContactLog => {
        if (!contactLogs) return null;
        const voicemails = contactLogs.filter((contactLog) => contactLog.value === ContactLogType.Voicemail);
        return voicemails[1];
    };

    public static getEmailPitchInContactLogs = (contactLogs: ContactLog[]): ContactLog => {
        if (!contactLogs) return null;
        return contactLogs.find((contactLog) => contactLog.value === ContactLogType.EmailPitch);
    };

    public static getEmailEnergyUrgencyContactLogs = (contactLogs: ContactLog[]): ContactLog => {
        if (!contactLogs) return null;
        return contactLogs.find((contactLog) => contactLog.value === ContactLogType.EmailEnergyUrgency);
    };

    public static getEmailMoverContactLogs = (contactLogs: ContactLog[]): ContactLog => {
        if (!contactLogs) return null;
        return contactLogs.find((contactLog) => contactLog.value === ContactLogType.EmailMover);
    };

    public static getEmailRealEstateAgentContactLogs = (contactLogs: ContactLog[]): ContactLog => {
        if (!contactLogs) return null;
        return contactLogs.find((contactLog) => contactLog.value === ContactLogType.EmailRealEstateAgent);
    };

    public static getMoveTransactionType(move: Move): MoveTransactionType | null {
        if (move.moveStates?.transactionType) return move.moveStates.transactionType;
        if (!move.user) return null;
        if (
            (move.user.role === MoverRole.Leaver && move.leaverType === Leaver.OwnerLeaver) ||
            (move.user.role === MoverRole.Transferee && move.user.transfereeType === Transferee.OwnerTransferee)
        )
            return MoveTransactionType.Sale;

        return MoveTransactionType.Rental;
    }

    public static telecomTransferSuccess = (telecomTransfer: TelecomTransfer, user: User): boolean => {
        return !!telecomTransfer?.fixedLine && !!telecomTransfer?.currentSupplier && !!user?.currentAddress;
    };

    public static hasTelecomInstallation = (move: Move): boolean => {
        return !!move?.telecomOffer?.telecomInstallation?.date && !!move?.telecomOffer?.telecomInstallation?.timeSlot;
    };

    public static handleCanOrderStateSelector = (
        energyOfferSelected,
        energyOrdered,
        telecomOfferSelected,
        telecomTransferSelected,
        telecomOrdered,
        props,
    ) => {
        // ALL
        if (props?.energy && props?.telecom) {
            return (
                (energyOfferSelected || !props.onlyOffers) &&
                !energyOrdered &&
                (telecomOfferSelected || (telecomTransferSelected && !props.onlyOffers)) &&
                !telecomOrdered
            );
        }

        // ONE (TELECOM)
        else if (!props?.energy && props?.telecom) {
            return (
                (telecomOfferSelected || (telecomTransferSelected && !props.onlyOffers)) &&
                !telecomOrdered &&
                (props.energy === false ? (!energyOfferSelected && !props?.onlyOffers) || energyOrdered : true)
            );
        }

        // ONE (ENERGY)
        else if (props?.energy && !props?.telecom) {
            return (
                (energyOfferSelected || !props.onlyOffers) &&
                !energyOrdered &&
                (props.telecom === false
                    ? (!telecomOfferSelected && (!telecomTransferSelected || props?.onlyOffers)) || telecomOrdered
                    : true)
            );
        }
    };

    public static mapToNpsReview(move: Move): Partial<NpsReview> {
        const { nps } = move.surveys;
        return {
            _id: undefined,
            score: nps.answers.score.value,
            suggestion: nps.answers.suggestion?.value,
            assessedTo: nps.assessedTo,
            isAnonymous: nps.answers.isAnonymous?.value,
            moveId: move._id,
            notes: nps.notes,
            source: ReviewSource.Smooved,
            language: move.user?.language,
            createdBy: {
                role: undefined,
                firstName: move.user?.firstName ? `${move.user.firstName?.substring(0, 1)}.` : undefined,
                lastName: move.user?.lastName,
                email: move.user?.email,
            },
        };
    }

    public static getOrders<T extends Order>(move: Move, scope: FeatureScope): T[] {
        return move.orders?.filter((order): boolean => order.scope.startsWith(scope)) as T[];
    }

    public static getMovers(move: Move): Movers {
        const linkedMove = ObjectUtils.isObject(move.linkedMove)
            ? {
                  ...(move.linkedMove as Move),
                  linkedMove: move,
              }
            : undefined;

        if (MoveUtils.isLeaver(move)) {
            return { leaver: move, transferee: linkedMove };
        } else {
            return { leaver: DbUtils.getStringId(linkedMove) ? linkedMove : undefined, transferee: move };
        }
    }

    public static isElectricityEnergyType(energyType: EnergyType): boolean {
        return [EnergyType.Both, EnergyType.Electricity].includes(energyType);
    }

    public static isGasEnergyType(energyType: EnergyType): boolean {
        return [EnergyType.Both, EnergyType.Gas].includes(energyType);
    }

    public static getElectricityAdvanceAmount(move: Move): number {
        if (move.electricityAdvanceAmount) return move.electricityAdvanceAmount;
        if (!move?.energyOffer?.energyOfferSelection) return null;
        return DateUtils.getMonthlyAverage(move.energyOffer.energyOfferSelection.electricityTotalPrice);
    }

    public static getGasAdvanceAmount(move: Move): number {
        if (move.gasAdvanceAmount) return move.gasAdvanceAmount;
        if (!move?.energyOffer?.energyOfferSelection) return null;
        return DateUtils.getMonthlyAverage(move.energyOffer.energyOfferSelection.gasTotalPrice);
    }

    public static isProfessional(move: Pick<Move, 'professional'>): boolean {
        return !!move?.professional;
    }

    public static checkForTodo(move: Move, todoType: TodoType): boolean {
        return !!move.todosForRealEstateAgent?.find((todoForRealEstateAgent) => todoForRealEstateAgent.todo === todoType);
    }

    public static isEnergyMetersState(move: Partial<Move>, ...args: EnergyMetersState[]): boolean {
        if (!move || ArrayUtils.isEmpty(args)) return false;
        return args.includes(move.track?.energyMeterReadings?.energyMetersState);
    }
}
